// Sub-pantalla "Corregir voz" del panel del clip en el Estudio de Video —
// ADR-2026-09-24-corregir-voz-es-un-boton-del-estudio.
//
// El mismo gesto que la sheet de Creaciones (tocar una palabra, escribir cómo tenía que
// decir), pero sobre un CLIP: sin velocidad (en el editor se cambia aparte) y sin video
// nuevo — el clip se reemplaza in-place con la misma `corregirVozEnClip` que usa el agente.
// El componente no habla con el backend: recibe el transcript del clip y dos callbacks.

import { useEffect, useMemo, useRef, useState } from "react";
import { AudioLines, Info, Loader2, RotateCcw } from "lucide-react";
import {
  edicionesDesde,
  esHablada,
  oracionesEnLaVentana,
  type EdicionLab,
  type PalabraLab,
} from "./transcript";

interface Props {
  /** `clip.transcript.words`: el transcript COMPLETO del asset, en tiempos del archivo. */
  palabras: PalabraLab[] | null;
  /** Lo que se escucha del archivo en la timeline, en segundos del archivo. */
  ventana: { desde: number; hasta: number };
  corrigiendo: boolean;
  /** Error de la última corrección (el mismo que muestra el badge del clip). */
  error?: string;
  /** Por qué no se puede corregir ahora (otra operación en curso, clip sin subir…). */
  bloqueo?: string;
  /** Transcribe el clip y lo guarda en él. Devuelve el mensaje de error, o null. */
  onTranscribir: () => Promise<string | null>;
  onCorregir: (ediciones: EdicionLab[]) => void;
}

export default function CorregirVozDelClip({
  palabras,
  ventana,
  corrigiendo,
  error,
  bloqueo,
  onTranscribir,
  onCorregir,
}: Props) {
  const [textos, setTextos] = useState<Record<number, string>>({});
  const [editando, setEditando] = useState<number | null>(null);
  const [transcribiendo, setTranscribiendo] = useState(false);
  const [errorTranscript, setErrorTranscript] = useState<string | null>(null);

  const hayPalabras = !!palabras && palabras.length > 0;

  // Sin transcript se pide solo al abrir, como en Creaciones: la pantalla no sirve sin él.
  // Una sola vez por montaje; el reintento es manual.
  const pidio = useRef(false);
  const transcribir = async () => {
    setErrorTranscript(null);
    setTranscribiendo(true);
    const err = await onTranscribir();
    setTranscribiendo(false);
    if (err) setErrorTranscript(err);
  };
  useEffect(() => {
    if (hayPalabras || pidio.current) return;
    pidio.current = true;
    void transcribir();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hayPalabras]);

  // Cuando el transcript cambia (se parchea al terminar una corrección o se re-transcribe),
  // lo tipeado queda viejo: los índices podrían ser de otro texto.
  useEffect(() => {
    setTextos({});
    setEditando(null);
  }, [palabras]);

  // Sólo las oraciones que se escuchan en la timeline (ver `oracionesEnLaVentana`).
  const { desde, hasta } = ventana;
  const oraciones = useMemo(
    () => (palabras ? oracionesEnLaVentana(palabras, { desde, hasta }) : []),
    [palabras, desde, hasta],
  );
  const ediciones = useMemo(() => (palabras ? edicionesDesde(palabras, textos) : []), [palabras, textos]);
  const sinVoz = hayPalabras && !palabras!.some(esHablada);
  const puedeAplicar = ediciones.length > 0 && !corrigiendo && !bloqueo;

  const aplicar = () => {
    if (!puedeAplicar) return;
    onCorregir(ediciones);
    setTextos({});
    setEditando(null);
  };

  if (transcribiendo) {
    return (
      <div className="flex items-center gap-2 py-6 justify-center text-[13px] text-brand-black/60" role="status">
        <Loader2 className="w-4 h-4 animate-spin text-brand-primary" />
        Leyendo lo que dice el clip…
      </div>
    );
  }

  if (!hayPalabras) {
    return (
      <div className="py-4 text-center">
        <p className="text-[13px] text-red-600 mb-3">
          {errorTranscript || "No pudimos leer lo que dice el clip."}
        </p>
        <button
          type="button"
          onClick={() => void transcribir()}
          className="py-2 px-4 rounded-lg border border-neutral-200 text-[13px] text-brand-black hover:bg-neutral-50"
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (sinVoz || oraciones.length === 0) {
    return (
      <p className="py-6 text-center text-[13px] text-brand-black/60">
        {sinVoz ? "No encontramos voz en este clip." : "En el tramo del clip que está en la timeline no se habla."}
      </p>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[12px] text-[#3C3C43]/60">Tocá una palabra para corregirla</p>
        {ediciones.length > 0 && !corrigiendo && (
          <button
            type="button"
            onClick={() => setTextos({})}
            className="inline-flex items-center gap-1 text-[12px] text-[#3C3C43]/60 hover:text-brand-black"
          >
            <RotateCcw className="w-3 h-3" /> Deshacer
          </button>
        )}
      </div>

      <div className="space-y-2">
        {oraciones.map((indices, o) => {
          const tocada = indices.some((i) => ediciones.some((e) => e.indice === i));
          return (
            <div
              key={o}
              className={`flex flex-wrap gap-1 p-2 rounded-lg border transition-colors ${
                tocada ? "border-[rgb(var(--brand-primary-rgb)_/_0.4)] bg-[rgb(var(--brand-primary-rgb)_/_0.07)]" : "border-neutral-200 bg-white"
              }`}
            >
              {indices.map((i) => {
                const p = palabras![i];
                const nuevo = textos[i]?.trim();
                const cambiado = !!nuevo && nuevo !== p.text.trim();
                if (editando === i) {
                  return (
                    <input
                      key={i}
                      autoFocus
                      defaultValue={textos[i] ?? p.text}
                      onBlur={(e) => {
                        setTextos((t) => ({ ...t, [i]: e.target.value }));
                        setEditando(null);
                      }}
                      // Las teclas no llegan a los atajos del editor (espacio = play,
                      // flechas = mover el cabezal, Backspace = borrar el clip).
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                        if (e.key === "Escape") setEditando(null);
                      }}
                      className="py-0.5 px-1.5 rounded-md border border-brand-primary text-[13px] w-24 outline-none"
                    />
                  );
                }
                return (
                  <button
                    key={i}
                    type="button"
                    disabled={corrigiendo}
                    onClick={() => setEditando(i)}
                    title={cambiado ? `Decía: ${p.text}` : "Tocá para corregir"}
                    className={`py-0.5 px-1.5 rounded-md text-[13px] leading-snug transition-colors disabled:cursor-wait ${
                      cambiado ? "bg-brand-primary text-white" : "text-brand-black hover:bg-neutral-100"
                    }`}
                  >
                    {cambiado ? nuevo : p.text}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={aplicar}
        disabled={!puedeAplicar}
        className="mt-4 h-9 w-full inline-flex items-center justify-center gap-1.5 rounded-md text-[13px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ backgroundColor: "rgb(var(--brand-primary-rgb) / 0.08)", color: "var(--brand-primary)" }}
      >
        {corrigiendo ? (
          <>
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Corrigiendo la voz…
          </>
        ) : (
          <>
            <AudioLines className="w-3.5 h-3.5" /> Corregir voz
          </>
        )}
      </button>

      {bloqueo && !corrigiendo && <p className="mt-2 text-[12px] text-neutral-500 leading-snug">{bloqueo}</p>}
      {error && !corrigiendo && <p className="mt-2 text-[12px] text-red-600 leading-snug">{error}</p>}

      <div className="mt-3 flex items-start gap-1.5 text-[12px] text-neutral-500 leading-snug">
        <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
        <span>
          {corrigiendo
            ? "Tarda entre 15 y 60 segundos. El clip se reemplaza solo cuando termina."
            : "Se vuelve a decir la oración con la misma voz del clip. El original queda en Archivos. No consume créditos."}
        </span>
      </div>
    </div>
  );
}
